import { ValueOrAsyncFunction, ValueOrFunction } from "./ValueOrFunctionTypes";

export class ValueResolver {
    /**
     * This is a convenience method to resolve an argument that could either be a property value or a function that could return
     * the property value.
     *
     * For example, we may want a boolean property like Component.visible that determines if a component should be shown.  Most of the
     * time, this property will just either either true or false, but we may want to be flexible enough to allow logic (a function) to determine whether
     * the component will be shown or not.  This makes it easier to support that case.
     *
     * @param prop The property value or function to evaluate
     * @param argsIfFunction If the argument happens to be a function, these are optional arguments to pass to that function.
     * @returns The property value after it has been resolved
     */
    public static resolve<T>(prop: ValueOrFunction<T>, ...argsIfFunction: any[]): T {
        if (typeof prop === "function")
            return (prop as any)(...argsIfFunction);
        else
            return prop;
    }

    /**
     * This is a convenience method to resolve an argument that could either be a property value or a function.  If the argument is a function
     * it can return a Promise that will be resolved.
     *
     * For example, we may want a boolean property like Component.visible that determines if a component should be shown.  Most of the
     * time, this property will just either either true or false, but we may want to be flexible enough to allow logic (a function) to determine whether
     * the component will be shown or not.  Then, assume that function needs to make an api call to determine if it should be shown or not.
     * This makes it easier to support that case.
     *
     * @param prop The property value or function to evaluate
     * @param argsIfFunction If the argument happens to be a function, these are optional arguments to pass to that function.
     * @returns The property value after it has been resolved
     */
    public static async resolveAsync<T>(prop: ValueOrAsyncFunction<T>, ...argsIfFunction: any[]): Promise<T> {
        if (typeof prop === "function") {
            const result = (prop as any)(...argsIfFunction);
            if (result instanceof Promise)
                return await result;
            else
                return Promise.resolve(result);
        }
        else
            return Promise.resolve(prop);
    }
}
